// La fase 2 de la generación del informe (valorización) sobre TanStack Query.
//
// Cada escritura devuelve la valorización ENTERA ya recalculada, así que se
// siembra en la cache con `setQueryData` en vez de invalidarla: un refetch
// repintaría la pantalla con un parpadeo para traer lo que ya llegó. Lo que sí
// se invalida es la hoja de hechos, porque el servidor reescribe ahí las cifras
// calculadas (valor por rubro, total, liquidación).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { hechosKey } from "@/hooks/useGeneracion";
import {
  aceptarPropuestaRequest,
  crearLineaManualRequest,
  eliminarLineaRequest,
  fetchValorizacion,
  fijarCastigosRequest,
  fijarPrecioRequest,
  fijarTipoDeCambioRequest,
  type CuerpoDeLineaManual,
  type CuerpoDelPrecio,
  type RubroDeValorizacion,
  type Valorizacion,
} from "@/lib/valorizacion";

export const valorizacionKey = (informeId: number) => ["generacion", informeId, "valorizacion"] as const;

export function useValorizacion(informeId: number, habilitada = true) {
  return useQuery({
    queryKey: valorizacionKey(informeId),
    queryFn: () => fetchValorizacion(informeId),
    enabled: habilitada && Number.isFinite(informeId) && informeId > 0,
  });
}

/** Siembra la valorización que devolvió una escritura y suelta la hoja de hechos. */
function useAlEscribir(informeId: number) {
  const queryClient = useQueryClient();

  return (valorizacion: Valorizacion) => {
    queryClient.setQueryData(valorizacionKey(informeId), valorizacion);
    void queryClient.invalidateQueries({ queryKey: hechosKey(informeId) });
  };
}

export function useFijarPrecio(informeId: number) {
  const alEscribir = useAlEscribir(informeId);

  return useMutation({
    mutationFn: ({ lineaId, cuerpo }: { lineaId: number; cuerpo: CuerpoDelPrecio }) =>
      fijarPrecioRequest(informeId, lineaId, cuerpo),
    onSuccess: alEscribir,
  });
}

export function useAceptarPropuesta(informeId: number) {
  const alEscribir = useAlEscribir(informeId);

  return useMutation({
    mutationFn: (lineaId: number) => aceptarPropuestaRequest(informeId, lineaId),
    onSuccess: alEscribir,
  });
}

export function useFijarCastigos(informeId: number) {
  const alEscribir = useAlEscribir(informeId);

  return useMutation({
    mutationFn: (cuerpo: { global?: number | null; por_rubro?: Partial<Record<RubroDeValorizacion, number | null>> }) =>
      fijarCastigosRequest(informeId, cuerpo),
    onSuccess: alEscribir,
  });
}

export function useCrearLineaManual(informeId: number) {
  const alEscribir = useAlEscribir(informeId);

  return useMutation({
    mutationFn: (cuerpo: CuerpoDeLineaManual) => crearLineaManualRequest(informeId, cuerpo),
    onSuccess: alEscribir,
  });
}

export function useEliminarLinea(informeId: number) {
  const alEscribir = useAlEscribir(informeId);

  return useMutation({
    mutationFn: (lineaId: number) => eliminarLineaRequest(informeId, lineaId),
    onSuccess: alEscribir,
  });
}

/**
 * Fijar el tipo de cambio escribe hechos, no devuelve la valorización: las
 * monedas de los totales dependen de él, así que ahí sí se vuelve a pedir.
 */
export function useFijarTipoDeCambio(informeId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fecha: string) => fijarTipoDeCambioRequest(informeId, fecha),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: valorizacionKey(informeId) }),
        queryClient.invalidateQueries({ queryKey: hechosKey(informeId) }),
      ]),
  });
}
